import { useEffect, useState } from "react";
import { Activity, Download, Gauge, Hourglass, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-xl border border-[rgba(245,197,66,0.2)] bg-black/40 p-3">
    <p className="flex items-center gap-1.5 text-[0.7rem] uppercase tracking-wider text-neutral-400"><Icon className="h-3.5 w-3.5 text-[var(--gold)]" /> {label}</p>
    <p className="mt-1 font-display text-lg font-black text-[var(--gold-bright)]" data-testid={testId}>{value}</p>
  </div>
);

export const QueueStatsCard = () => {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [tier, setTier] = useState(null);
  useEffect(() => {
    const load = () => {
      api.get("/admin/queue").then(({ data }) => setS(data)).catch(() => {});
      api.get("/admin/queue-tier").then(({ data }) => setTier(data)).catch(() => {});
    };
    load();
    const id = setInterval(load, 5000);
    window.addEventListener("fmo-tier", load);
    return () => {
      clearInterval(id);
      window.removeEventListener("fmo-tier", load);
    };
  }, []);
  return (
    <section className="card-carbon rounded-2xl p-6" data-testid="queue-stats-card">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white">
        <Activity className="h-5 w-5 text-[var(--gold)]" /> {t("qadmin.title")}
      </h2>
      {s && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <Stat icon={Users} label={t("qadmin.active")} value={`${s.active} / ${s.max_active}`} testId="queue-stats-active" />
            <Stat icon={Hourglass} label={t("qadmin.waiting")} value={`${s.waiting} / ${s.max_waiting}`} testId="queue-stats-waiting" />
            <Stat icon={Download} label={t("qadmin.downloading")} value={s.downloading} testId="queue-stats-downloading" />
            {tier && <Stat icon={Gauge} label={t("tier.effective")} value={tier.effective} testId="queue-stats-effective" />}
          </div>
          {tier && (
            <p className={`mt-3 text-xs ${tier.throttled ? "text-amber-200" : "text-neutral-400"}`} data-testid="queue-stats-tier">
              {t("tier.limitLine", { eff: tier.effective, lim: tier.tier_limit, tier: tier.tier === "custom" ? t("tier.custom") : tier.tier ?? "—" })}
              {tier.throttled && ` · ${t("tier.throttled")}`}
            </p>
          )}
          {tier?.saturation?.suggest_next && (
            <p className="mt-2 text-xs font-semibold text-[var(--gold-bright)]" data-testid="queue-stats-saturation">{t("tier.saturation", { pct: Math.round(tier.saturation.full_pct) })}</p>
          )}
          <p className="mt-4 text-xs text-neutral-400" data-testid="queue-stats-idle">
            {s.idle_seconds < 60 ? t("qadmin.idleSec", { n: s.idle_seconds }) : t("qadmin.idle", { n: Math.round(s.idle_seconds / 6) / 10 })}
          </p>
          <p className="mt-1 text-xs text-neutral-500">{t("qadmin.adminNote")}</p>
        </>
      )}
    </section>
  );
};
